/**
 * ComponenteSelecionadorDocumentos - Seletor de Documentos para Análise
 * 
 * CONTEXTO DE NEGÓCIO:
 * Permite ao usuário selecionar especificamente quais documentos devem ser
 * usados como contexto RAG durante uma análise multi-agent. Ao invés de
 * buscar em todos os documentos disponíveis, o sistema filtrará apenas
 * os documentos selecionados.
 * 
 * FUNCIONALIDADES:
 * - Busca lista de documentos disponíveis (GET /api/documentos/listar)
 * - Exibe checkboxes com documentos e seus metadados
 * - Botões "Selecionar Todos" / "Limpar Seleção"
 * - Estado de loading durante busca
 * - Tratamento de erros
 * - Callback para notificar mudanças de seleção
 * - Indicador visual de quantos documentos estão selecionados
 * 
 * INTEGRAÇÃO:
 * - API: GET /api/documentos/listar (via servicoApiDocumentos.ts)
 * - Backend: TAREFA-022 (campo documento_ids no endpoint de análise)
 * - Componente pai: PaginaAnalise.tsx
 * 
 * COMPORTAMENTO:
 * - Se nenhum documento for selecionado: análise busca em TODOS os documentos (padrão)
 * - Se documento(s) for(em) selecionado(s): análise busca APENAS nos selecionados
 * 
 * VALIDAÇÕES:
 * - Exibe apenas documentos com status "concluido" (processamento completo)
 * - Se não houver documentos, exibe mensagem orientando o usuário
 * 
 * RELACIONADO COM:
 * - TAREFA-022: Backend - Seleção de Documentos na Análise
 * - TAREFA-021: Página de Histórico de Documentos
 * - backend/src/api/modelos.py (RequestAnaliseMultiAgent.documento_ids)
 * - backend/src/agentes/agente_advogado_coordenador.py (filtro RAG)
 */

import { useState, useEffect } from 'react';
import {
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XSquare,
  Calendar,
  FileCheck,
} from 'lucide-react';
import { listarDocumentos, deletarDocumento, renomearDocumento } from '../../servicos/servicoApiDocumentos';
import type { DocumentoListado } from '../../tipos/tiposDocumentos';
import { ComponenteUploadDocumentos } from '../upload/ComponenteUploadDocumentos';
import { useModal } from '../../../../core/contextos/modalContext';
import { Tooltip } from '../../../../core/componentes/Tooltip';
import IconButton from '../../../../core/componentes/comuns/IconButton';
import { Pencil, Trash2, UploadCloud } from 'lucide-react';


// ===== TIPOS =====

/**
 * Props do componente
 */
interface PropsComponenteSelecionadorDocumentos {
  /**
   * Callback chamado quando a seleção de documentos muda
   * 
   * CONTEXTO:
   * Componente pai (PaginaAnalise) precisa saber quais documentos
   * estão selecionados para enviar na requisição de análise.
   * 
   * PARÂMETRO:
   * - documentosSelecionados: Array de IDs de documentos selecionados
   *   Se vazio, análise usará todos os documentos (comportamento padrão)
   */
  aoAlterarSelecao: (documentosSelecionados: string[]) => void;

  /**
   * Se deve exibir validação visual (borda vermelha se inválido)
   * 
   * CONTEXTO:
   * Quando usuário tenta enviar análise sem preencher campos obrigatórios,
   * componente pai ativa validação visual. Este componente não é obrigatório,
   * então não há validação de erro, mas mantemos prop para consistência.
   */
  exibirValidacao?: boolean;

  /**
   * Callback para notificar o pai que um upload foi concluído.
   */
  aoFinalizarUpload?: () => void;
}

/**
 * Estado de carregamento do componente
 */
type EstadoCarregamento = 'idle' | 'loading' | 'success' | 'error';


// ===== COMPONENTE PRINCIPAL =====

/**
 * Componente de seleção de documentos para análise
 * 
 * IMPLEMENTAÇÃO:
 * - useEffect para buscar documentos ao montar
 * - useState para gerenciar lista de documentos e seleção
 * - Checkboxes para cada documento
 * - Botões de "Selecionar Todos" e "Limpar Seleção"
 * - Filtro para mostrar apenas documentos processados com sucesso
 * - Loading state com spinner
 * - Error state com mensagem e retry
 */
export function ComponenteSelecionadorDocumentos({
  aoAlterarSelecao,
  aoFinalizarUpload,
}: PropsComponenteSelecionadorDocumentos) {
  const { mostrarAlerta, mostrarConfirmacao } = useModal();
  // ===== ESTADO =====

  /**
   * Lista de todos os documentos disponíveis (apenas concluídos)
   */
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState<
    DocumentoListado[]
  >([]);

  /**
   * Set de IDs de documentos selecionados
   * 
   * CONTEXTO:
   * Usando Set para performance em verificações de seleção (O(1)).
   * Convertido para Array ao notificar componente pai.
   */
  const [documentosSelecionados, setDocumentosSelecionados] = useState<Set<string>>(
    new Set()
  );

  /**
   * Estado de carregamento da busca de documentos
   */
  const [estadoCarregamento, setEstadoCarregamento] = useState<EstadoCarregamento>('idle');

  /**
   * Mensagem de erro (quando erro)
   */
  const [mensagemErro, setMensagemErro] = useState<string>('');


  // ===== EFFECTS =====

  /**
   * Buscar lista de documentos ao montar o componente
   * 
   * CONTEXTO:
   * Executa uma vez quando componente é montado.
   * Busca documentos via API e filtra apenas os processados com sucesso.
   */
  useEffect(() => {
    buscarDocumentos();
  }, []);

  /**
   * Notificar componente pai quando seleção mudar
   * 
   * CONTEXTO:
   * Sempre que o usuário marcar/desmarcar um documento,
   * notificamos o componente pai com a lista atualizada.
   */
  useEffect(() => {
    aoAlterarSelecao(Array.from(documentosSelecionados));
  }, [documentosSelecionados, aoAlterarSelecao]);


  // ===== FUNÇÕES AUXILIARES =====

  /**
   * Buscar lista de documentos da API
   * 
   * FLUXO:
   * 1. Ativa loading state
   * 2. Chama GET /api/documentos/listar
   * 3. Filtra apenas documentos com status "concluido"
   * 4. Atualiza estado com documentos disponíveis
   * 5. Se erro, exibe mensagem de erro
   */
  const buscarDocumentos = async () => {
    setEstadoCarregamento('loading');
    setMensagemErro('');

    try {
      const resposta = await listarDocumentos();
      // Alguns ambientes não retornam statusProcessamento no item; neste caso, considerar disponível por padrão
      const documentosNormalizados = resposta.documentos.filter((doc) => {
        return doc.statusProcessamento ? doc.statusProcessamento === 'concluido' : true;
      });
      setDocumentosDisponiveis(documentosNormalizados);
      setEstadoCarregamento('success');
    } catch (erro) {
      console.error('Erro ao buscar documentos:', erro);
      setEstadoCarregamento('error');
      setMensagemErro(
        erro instanceof Error
          ? erro.message
          : 'Erro desconhecido ao buscar documentos'
      );
    }
  };

  /**
   * Formatar data de upload para exibição
   * 
   * CONTEXTO:
   * Converte ISO string para formato brasileiro legível
   * 
   * @param dataISO - Data em formato ISO 8601
   * @returns String formatada (dd/mm/yyyy HH:MM)
   */
  const formatarDataUpload = (dataISO: string): string => {
    if (!dataISO) return '-';
    try {
      const data = new Date(dataISO);
      if (isNaN(data.getTime())) return '-';
      return data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  /**
   * Callback: Upload concluído com sucesso (inline)
   * - Atualiza a lista
   * - Pré-seleciona os novos IDs
   */
  const handleUploadConcluido = async (idsNovos: string[]) => {
    try {
      await buscarDocumentos();
      if (idsNovos && idsNovos.length > 0) {
        setDocumentosSelecionados((prev) => {
          const novoSet = new Set(prev);
          idsNovos.forEach((id) => novoSet.add(id));
          return novoSet;
        });
        mostrarAlerta({
          titulo: 'Upload concluído',
          mensagem: `${idsNovos.length} documento(s) adicionado(s) e pré-selecionado(s).`,
        });
      }
      // Notifica o componente pai sobre o upload
      if (aoFinalizarUpload) {
        aoFinalizarUpload();
      }
    } catch (e) {
      console.error('Erro ao atualizar lista após upload:', e);
    }
  };

  /**
   * Deletar um documento com confirmação
   */
  const handleDeletarDocumento = (idDocumento: string, nomeArquivo: string) => {
    mostrarConfirmacao({
      titulo: 'Excluir documento',
      mensagem: `Tem certeza que deseja excluir o documento "${nomeArquivo}"? Esta ação não pode ser desfeita.`,
      textoConfirmar: 'Excluir',
      textoCancelar: 'Cancelar',
      aoConfirmar: async () => {
        try {
          await deletarDocumento(idDocumento);
          // Remover da seleção e lista localmente para melhor UX
          setDocumentosSelecionados((prev) => {
            const novo = new Set(prev);
            novo.delete(idDocumento);
            return novo;
          });
          setDocumentosDisponiveis((lista) => lista.filter((d) => d.idDocumento !== idDocumento));
          mostrarAlerta({
            titulo: 'Documento excluído',
            mensagem: `"${nomeArquivo}" foi removido com sucesso.`,
          });
        } catch (erro) {
          const msg = erro instanceof Error ? erro.message : 'Erro ao excluir documento';
          mostrarAlerta({ titulo: 'Erro', mensagem: msg });
        }
      }
    });
  };

  /**
   * Renomear um documento (solicita novo nome via prompt simples)
   */
  const handleRenomearDocumento = async (idDocumento: string, nomeAtual: string) => {
    const valor = window.prompt('Novo nome do documento (sem alterar extensão):', nomeAtual.replace(/\.[^.]+$/, ''));
    if (valor === null) return; // cancelado
    const novoNomeBase = valor.trim();
    if (!novoNomeBase) {
      mostrarAlerta({ titulo: 'Nome inválido', mensagem: 'Por favor, informe um nome válido.' });
      return;
    }
    try {
      const resposta = await renomearDocumento(idDocumento, novoNomeBase);
      // Atualizar lista local rapidamente
      setDocumentosDisponiveis((lista) =>
        lista.map((d) =>
          d.idDocumento === idDocumento ? { ...d, nomeArquivo: resposta.nome_arquivo_novo } as DocumentoListado : d
        )
      );
      mostrarAlerta({ titulo: 'Documento renomeado', mensagem: `Novo nome: ${resposta.nome_arquivo_novo}` });
    } catch (erro) {
      const msg = erro instanceof Error ? erro.message : 'Erro ao renomear documento';
      mostrarAlerta({ titulo: 'Erro', mensagem: msg });
    }
  };


  // ===== HANDLERS =====

  /**
   * Handler: Alternar seleção de um documento
   * 
   * CONTEXTO:
   * Chamado quando usuário clica em checkbox de um documento.
   * Se estava selecionado, remove da seleção.
   * Se não estava, adiciona à seleção.
   * 
   * @param idDocumento - ID do documento a ser alternado
   */
  const handleToggleDocumento = (idDocumento: string) => {
    setDocumentosSelecionados((prevSelecionados) => {
      const novosSelecionados = new Set(prevSelecionados);
      
      if (novosSelecionados.has(idDocumento)) {
        // Remover se já estava selecionado
        novosSelecionados.delete(idDocumento);
      } else {
        // Adicionar se não estava selecionado
        novosSelecionados.add(idDocumento);
      }
      
      return novosSelecionados;
    });
  };

  /**
   * Handler: Selecionar todos os documentos
   * 
   * CONTEXTO:
   * Atalho para marcar todos os documentos disponíveis de uma vez.
   */
  const handleSelecionarTodos = () => {
    const todosIds = documentosDisponiveis.map((doc) => doc.idDocumento);
    setDocumentosSelecionados(new Set(todosIds));
  };

  /**
   * Handler: Limpar seleção (desmarcar todos)
   * 
   * CONTEXTO:
   * Atalho para desmarcar todos os documentos.
   * Análise voltará a buscar em TODOS os documentos (comportamento padrão).
   */
  const handleLimparSelecao = () => {
    setDocumentosSelecionados(new Set());
  };


  // ===== RENDERIZAÇÃO CONDICIONAL =====

  /**
   * Estado: Loading (buscando documentos)
   */
  if (estadoCarregamento === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center space-y-3 py-12">
        <Loader2 className="size-8 animate-spin text-blue-500" />
        <p className="text-sm text-gray-600">Carregando documentos disponíveis...</p>
      </div>
    );
  }

  /**
   * Estado: Erro ao buscar documentos
   */
  if (estadoCarregamento === 'error') {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertCircle className="size-6" />
          <p className="font-medium">Erro ao carregar documentos</p>
        </div>
        <p className="max-w-md text-center text-sm text-gray-600">
          {mensagemErro}
        </p>
        <button
          onClick={buscarDocumentos}
          className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  /**
   * Estado: Nenhum documento disponível
   */
  if (documentosDisponiveis.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-gray-700">
          <UploadCloud className="size-5" />
          <span className="font-medium">Nenhum documento disponível — envie arquivos abaixo</span>
        </div>
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
          <ComponenteUploadDocumentos
            aoFinalizarUploadComSucesso={(ids) => handleUploadConcluido(ids)}
          />
        </div>
      </div>
    );
  }


  // ===== RENDERIZAÇÃO PRINCIPAL =====

  const totalDocumentos = documentosDisponiveis.length;
  const totalSelecionados = documentosSelecionados.size;
  const todosEstaoSelecionados = totalSelecionados === totalDocumentos;

  return (
    <div className="space-y-4">
      {/* Área de Upload Inline */}
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="mb-3 flex items-center gap-2 text-gray-700">
          <UploadCloud className="size-5" />
          <span className="font-medium">Adicionar documentos</span>
        </div>
        <ComponenteUploadDocumentos
          aoFinalizarUploadComSucesso={(ids) => handleUploadConcluido(ids)}
        />
      </div>
      {/* Cabeçalho com informações e botões de ação */}
      <div className="flex items-center justify-between">
        {/* Contador de seleção */}
        <div className="flex items-center space-x-2">
          <FileCheck className="size-5 text-gray-600" />
          <p className="text-sm text-gray-700">
            {totalSelecionados === 0 ? (
              <span>
                <strong>Nenhum documento selecionado</strong> (análise usará todos os {totalDocumentos} documentos)
              </span>
            ) : (
              <span>
                <strong>{totalSelecionados}</strong> de <strong>{totalDocumentos}</strong> documento(s) selecionado(s)
              </span>
            )}
          </p>
        </div>

        {/* Botões de ação */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSelecionarTodos}
            disabled={todosEstaoSelecionados}
            className={`
              flex items-center space-x-1 rounded-lg px-3 py-1.5 text-sm
              transition-colors
              ${
                todosEstaoSelecionados
                  ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }
            `}
            title="Selecionar todos os documentos"
          >
            <CheckSquare className="size-4" />
            <span>Selecionar Todos</span>
          </button>

          <button
            onClick={handleLimparSelecao}
            disabled={totalSelecionados === 0}
            className={`
              flex items-center space-x-1 rounded-lg px-3 py-1.5 text-sm
              transition-colors
              ${
                totalSelecionados === 0
                  ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
            title="Limpar seleção (análise usará todos os documentos)"
          >
            <XSquare className="size-4" />
            <span>Limpar Seleção</span>
          </button>
        </div>
      </div>

      {/* Info sobre comportamento */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <p className="text-xs text-blue-800">
          <strong>💡 Dica:</strong> Selecione documentos específicos para focar a análise apenas neles.
          Se nenhum documento for selecionado, a análise considerará todos os documentos disponíveis.
        </p>
      </div>

      {/* Lista de documentos com checkboxes */}
      <div className="max-h-96 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-300">
        {documentosDisponiveis.map((documento) => {
          const estaSelecionado = documentosSelecionados.has(documento.idDocumento);

          return (
            <div
              key={documento.idDocumento}
              onClick={() => handleToggleDocumento(documento.idDocumento)}
              className={`
                flex cursor-pointer items-start space-x-3 p-4
                transition-colors hover:bg-gray-50
                ${estaSelecionado ? 'bg-blue-50' : 'bg-white'}
              `}
            >
              {/* Checkbox */}
              <div className="shrink-0 pt-1">
                {estaSelecionado ? (
                  <CheckCircle2 className="size-5 text-blue-600" />
                ) : (
                  <Square className="size-5 text-gray-400" />
                )}
              </div>

              {/* Informações do documento */}
              <div className="min-w-0 flex-1">
                {/* Nome do arquivo */}
                <p className={`
                  truncate font-medium
                  ${estaSelecionado ? 'text-blue-900' : 'text-gray-900'}
                `}>
                  {documento.nomeArquivo}
                </p>

                {/* Metadados */}
                <div className="mt-1 flex items-center space-x-4 text-xs text-gray-600">
                  {/* Data de upload */}
                  <div className="flex items-center space-x-1">
                    <Calendar className="size-3" />
                    <span>{formatarDataUpload(documento.dataHoraUpload)}</span>
                  </div>

                  {/* Tipo (após data). Removemos a exibição do tamanho conforme solicitado */}
                  <span>•</span>
                  <span className="uppercase">{documento.tipoDocumento}</span>
                </div>

                {/* ID do documento (para debug/rastreabilidade) */}
                <p className="mt-1 truncate font-mono text-xs text-gray-400">
                  ID: {documento.idDocumento}
                </p>
              </div>

              {/* Ações por item (Renomear/Excluir) */}
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Tooltip text="Renomear">
                  <IconButton srLabel="Renomear documento" onClick={() => handleRenomearDocumento(documento.idDocumento, documento.nomeArquivo)}>
                    <Pencil className="size-5" />
                  </IconButton>
                </Tooltip>
                <Tooltip text="Excluir">
                  <IconButton srLabel="Excluir documento" onClick={() => handleDeletarDocumento(documento.idDocumento, documento.nomeArquivo)}>
                    <Trash2 className="size-5" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
